<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { localeMappings } from '~/i18n/config';
import { useRoute, useRouter, ref, computed, onMounted, onUnmounted, onActivated, onDeactivated, useSession, useHead, watch, useRuntimeConfig, useSeoMeta, setResponseStatus, useResponseHeader } from '#imports';
import { useAuthState } from '~/composables/useAuthState';
import { useToast } from '~/composables/useToast';
import { musicService } from '~/services/musicService';
import { useMusicCreate } from '~/composables/useMusicCreate';
import { seoLandingPages } from '~/services/seoLandingConfig';
import type { SeoLandingPage } from '~/services/seoLandingConfig';
import { trackEvent } from '~/utils/analytics';
import { usePaywall } from '~/composables/usePaywall';
import { pricingPath } from '~/utils/checkout-source.mjs';
import { usePageReadingPosition } from '~/composables/usePageReadingPosition';
import { musicCoverUrl, musicCoverSrcset } from '~/utils/music-cover';

const { user, userAccount, handleSignIn, refreshUserAccount, isAuthReady, isAuthenticated, fetchAndStoreToken } = useAuthState();
const { openPaywall } = usePaywall();
const { session } = useSession();

const toast = useToast();
const siteUrl = useRuntimeConfig().public.siteUrl || 'https://musicmint.ai';
const { saveMusicData } = useMusicCreate();

// 定义统一的音乐视图模型
interface MusicViewModel {
  id: string;
  title: string;
  image_url: string;
  audio_url: string;
  display_name: string;
  releaseDate: string;
  duration: number;
  tags: string;
  description: string;
}

type PublicMusicDetailStatus = 'available' | 'gone' | 'notFound' | 'unavailable' | 'skipped';
const SONG_SSR_TIMEOUT_MS = 2500;

interface PublicMusicDetailResult {
  status: PublicMusicDetailStatus;
  data: any | null;
}

interface LyricsSection {
  heading: string;
  lines: string[];
}

const route = useRoute();
const router = useRouter();
const musicId = computed(() => route.params.id);
const taskId = computed(() => route.query.taskId as string || '');
const { t, locale } = useI18n();

// 状态
const isLoading = ref(true);
const musicLoadError = ref('');
const readingPath = route.path;
const isPrivateSong = computed(() => !!taskId.value);
const songTargetKey = computed(() => JSON.stringify([musicId.value?.toString() || '', taskId.value]));
const songSessionIdentity = computed(() => JSON.stringify([
  isAuthReady.value, isAuthenticated.value, user.value?.id || '', session.value?.id || '',
]));
const canReadPrivateSong = computed(() => isAuthReady.value && isAuthenticated.value && !!user.value?.id && !!session.value?.id);
const needsSongSignIn = computed(() => isPrivateSong.value && isAuthReady.value && !isAuthenticated.value);
const songPageActive = ref(false);
let songHasMounted = false;
const { pageRoot: songRoot, pageHeading: songHeading, restoreAfterLoad } = usePageReadingPosition(() => readingPath, { ready: () => !isLoading.value });
watch(isLoading, loading => { if (!loading) void restoreAfterLoad(); });
let musicDataRequest = 0;
let musicDataController: AbortController | undefined;
let loadedPrivateIdentity = '';
let loadedSongTarget = '';
const music = ref<MusicViewModel | null>(null);
const taskVersions = ref<Array<{ id: string; duration: number; position: number; image_url?: string }>>([]);
const versionCoverErrors = ref<Record<string, boolean>>({});
const currentVersion = computed(() => taskVersions.value.find(version => version.id === music.value?.id));
const songStyleTags = computed(() => {
  if (!music.value?.tags || music.value.tags === t('music.unknown')) return [];
  return [...new Set(music.value.tags.split(',').map(tag => tag.trim()).filter(Boolean))].slice(0, 4);
});
const loadedTaskOwnerId = ref('');
const taskHasDisplayName = ref(false);
const coverError = ref(false);
const desktopCoverImage = ref<HTMLImageElement | null>(null);
watch(() => music.value?.image_url, () => { coverError.value = false; });
const coverSrc = computed(() => coverError.value ? music.value?.image_url || '' : musicCoverUrl(music.value?.image_url || '', 400));
const coverSrcset = computed(() => coverError.value ? undefined : musicCoverSrcset(music.value?.image_url || '', [160, 200, 320, 400, 600]));
const coverSizes = '(min-width: 1280px) 360px, (min-width: 1024px) 320px, (min-width: 768px) 240px, 260px';
useHead(() => music.value?.image_url ? {
  link: [{ key: 'song-cover', rel: 'preload', as: 'image', href: coverSrc.value, imagesrcset: coverSrcset.value, imagesizes: coverSizes, fetchpriority: 'high' }],
} : {});
const isPlaying = ref(false);
const isAudioLoading = ref(false);
const isUnavailable = computed(() => !isLoading.value && !music.value && !musicLoadError.value);
const lyricsSections = computed<LyricsSection[]>(() => {
  const rawLyrics = music.value?.description?.trim();
  if (!rawLyrics) return [];

  const sections: LyricsSection[] = [];
  let current: LyricsSection = { heading: '', lines: [] };

  const flushSection = () => {
    if (current.heading || current.lines.length > 0) {
      sections.push(current);
      current = { heading: '', lines: [] };
    }
  };

  rawLyrics
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(line => line.trim())
    .forEach((line) => {
      if (!line) {
        return;
      }

      const sectionMatch = line.match(/^\[(.+?)\]$/);
      if (sectionMatch) {
        flushSection();
        current.heading = sectionMatch[1];
        return;
      }

      current.lines.push(line);
    });

  flushSection();
  return sections;
});

const getPublicMusicDetailErrorStatus = (error: any): number | null => {
  const status = Number(error?.response?.status || error?.response?.data?.code);
  return Number.isFinite(status) ? status : null;
};

// 从Music或GenMusic转换为统一的MusicViewModel
const convertToViewModel = (data: any): MusicViewModel => {
  // 根据字段判断是GenMusic还是Music
  const isGenMusic = 'audio_oss' in data || 'stream_audio_url' in data;

  return {
    id: data.id || '',
    title: data.title || '',
    image_url: data.image_url || '',
    audio_url: isGenMusic
      ? (data.audio_oss || data.stream_audio_url || data.audio_url || '')
      : (data.audio_url || ''),
    display_name: data.display_name || data.creator_name || data.user_name || t('ux.creatorUnavailable'),
    releaseDate: data.created_at || data.releaseDate || '',
    duration: isGenMusic
      ? (data.metadata?.duration || 0)
      : (data.metadata?.duration || data.upvote_count || 0),
    tags: data.metadata?.tags || t('music.unknown'),
    description: data.metadata?.prompt || ''
  };
};

// SSR 预取：公开歌曲（无 taskId）在服务端获取数据，确保爬虫能拿到正确 title
const { data: ssrMusicResult } = await useAsyncData<PublicMusicDetailResult>(
  `song-${route.params.id}`,
  async () => {
    // taskId 是用户私有任务数据，只在客户端获取
    if (route.query.taskId) return { status: 'skipped', data: null };
    const id = route.params.id as string;
    if (!id) return { status: 'notFound', data: null };
    try {
      const result = await musicService.getPublicMusicDetail(id, { timeout: SONG_SSR_TIMEOUT_MS });
      if (result?.code === 200 && result.data) {
        return { status: 'available', data: result.data };
      }
      if (result?.code === 410) return { status: 'gone', data: null };
      if (result?.code === 404) return { status: 'notFound', data: null };
    } catch (error: any) {
      const status = getPublicMusicDetailErrorStatus(error);
      if (status === 410) return { status: 'gone', data: null };
      if (status === 404) return { status: 'notFound', data: null };
    }
    return { status: 'unavailable', data: null };
  }
);

const unavailableResponseStatus = computed(() => {
  if (ssrMusicResult.value?.status === 'gone') return 410;
  if (ssrMusicResult.value?.status === 'notFound') return 404;
  return null;
});

if (import.meta.server && unavailableResponseStatus.value) {
  const status = unavailableResponseStatus.value;
  setResponseStatus(status, status === 410 ? 'Gone' : 'Not Found');
  useResponseHeader('X-Robots-Tag').value = 'noindex, nofollow, noarchive';
}

// Public SSR data initializes the same refs used by reactive head metadata.
if (ssrMusicResult.value?.status === 'available' && ssrMusicResult.value.data) {
  music.value = convertToViewModel(ssrMusicResult.value.data);
  loadedSongTarget = songTargetKey.value;
  isLoading.value = false;
} else if (!taskId.value && ssrMusicResult.value?.status !== 'unavailable') {
  isLoading.value = false;
}

useSeoMeta({
  robots: () => unavailableResponseStatus.value ? 'noindex,nofollow,noarchive' : 'index,follow',
});

useHead(() => isUnavailable.value ? {
  title: t('music.unavailableMetaTitle'),
  meta: [
    {
      name: 'description',
      content: t('music.unavailableMetaDescription'),
    },
    { property: 'og:title', content: t('music.unavailableMetaTitle') },
    {
      property: 'og:description',
      content: t('music.unavailableMetaDescription'),
    },
  ],
} : {});

// 加载音乐数据（仅用于 taskId 情况，或 SSR 未成功时的客户端兜底）
const syncTaskCreatorName = () => {
  if (!taskId.value || !music.value || taskHasDisplayName.value || !loadedTaskOwnerId.value || loadedTaskOwnerId.value !== userAccount.value?.id) return;
  const name = user.value?.fullName || user.value?.username;
  if (name && name !== music.value.display_name) music.value = { ...music.value, display_name: name };
};
watch([() => userAccount.value?.id, () => user.value?.fullName, () => user.value?.username], syncTaskCreatorName);
const loadMusicData = async () => {
  if (!songPageActive.value || route.path !== readingPath) return;
  if (isPrivateSong.value && !canReadPrivateSong.value) {
    isLoading.value = !isAuthReady.value || isAuthenticated.value;
    return;
  }
  const request = ++musicDataRequest;
  musicDataController?.abort();
  const controller = new AbortController();
  musicDataController = controller;
  const targetMusicId = musicId.value?.toString() || '';
  const targetTaskId = taskId.value;
  const identity = targetTaskId ? songSessionIdentity.value : '';
  const stillCurrent = () => request === musicDataRequest && !controller.signal.aborted
    && songPageActive.value && route.path === readingPath
    && targetMusicId === (musicId.value?.toString() || '') && targetTaskId === taskId.value
    && (!targetTaskId || (canReadPrivateSong.value && identity === songSessionIdentity.value));
  isLoading.value = true;
  musicLoadError.value = '';
  taskVersions.value = [];
  try {
    // 如果存在taskId，则通过getMusicTaskResult获取任务
    if (targetTaskId) {
      const token = await fetchAndStoreToken();
      if (!stillCurrent()) return;
      if (!token) throw new Error('Song session unavailable');
      const taskResult = await musicService.getMusicTaskResult({ task_id: targetTaskId }, undefined, {
        signal: controller.signal, musicmintInlineError: true,
      });
      if (!stillCurrent()) return;
      if (taskResult?.code === 404 || taskResult?.code === 410) { music.value = null; return; }
      if (taskResult?.code !== 200) throw new Error('Music unavailable');

      // 确保music_data是数组
      const musicDataArray = Array.isArray(taskResult?.data?.music_data)
        ? taskResult.data.music_data
        : (taskResult?.data?.music_data ? [taskResult.data.music_data] : []);

      if (musicDataArray.length > 0) {
        // 找到对应musicId的音乐，如果没指定则取第一个
        const targetMusic = targetMusicId
          ? musicDataArray.find((item: any) => item.id === targetMusicId)
          : musicDataArray[0];

        if (targetMusic) {
          taskVersions.value = musicDataArray.map((item: any, position: number) => ({
            id: item.id || '', duration: item.metadata?.duration || 0, position,
            ...(item.image_url ? { image_url: item.image_url } : {}),
          })).filter((version: { id: string }) => Boolean(version.id));
          loadedPrivateIdentity = identity;
          loadedSongTarget = songTargetKey.value;
          loadedTaskOwnerId.value = taskResult.data.user_id || '';
          taskHasDisplayName.value = Boolean(targetMusic.display_name);
          const ownerName = taskResult.data.user_id === userAccount.value?.id
            ? (user.value?.fullName || user.value?.username || '') : '';
          music.value = convertToViewModel({ ...targetMusic, display_name: targetMusic.display_name || ownerName });
          isLoading.value = false;
          return;
        }
      }
      music.value = null;
    } else if (targetMusicId && !music.value) {
      // SSR 未成功时的客户端兜底
      const musicData = await musicService.getPublicMusicDetail(targetMusicId, {
        signal: controller.signal, musicmintInlineError: true,
      });
      if (!stillCurrent()) return;
      if (musicData?.code === 200 && musicData.data) {
        music.value = convertToViewModel(musicData.data);
        loadedSongTarget = songTargetKey.value;
        loadedPrivateIdentity = '';
      } else if (musicData?.code === 404 || musicData?.code === 410 || musicData?.code === 200 && !musicData.data) {
        music.value = null;
      } else {
        throw new Error('Music unavailable');
      }
    }
  } catch (error) {
    if (stillCurrent()) {
      music.value = null;
      const status = getPublicMusicDetailErrorStatus(error);
      if (status !== 404 && status !== 410) musicLoadError.value = t('common.load_failed');
    }
  } finally {
    if (stillCurrent()) isLoading.value = false;
  }
};

const invalidateSongRead = () => {
  ++musicDataRequest;
  musicDataController?.abort();
};
const resetSongSession = () => {
  cancelSongDownload();
  downloadError.value = '';
  downloadNeedsMembership.value = false;
  if (!isPrivateSong.value && !loadedPrivateIdentity) return;
  invalidateSongRead();
  music.value = null;
  taskVersions.value = [];
  loadedTaskOwnerId.value = '';
  taskHasDisplayName.value = false;
  loadedPrivateIdentity = '';
  loadedSongTarget = '';
  musicLoadError.value = '';
  isPlaying.value = false;
  isAudioLoading.value = false;
  isLoading.value = !isAuthReady.value || isAuthenticated.value;
  if (songHasMounted && songPageActive.value && route.path === readingPath && canReadPrivateSong.value) void loadMusicData();
};

// 格式化时间
const formatDuration = (seconds: number | undefined) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 格式化日期
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value, { year: 'numeric', month: 'long', day: 'numeric' });
};

// 播放音乐
const playMusic = () => {
  if (!music.value) return;

  if (!isPlaying.value && !isAudioLoading.value) {
    // 创建一个包含必要播放信息的对象
    const playInfo = {
      id: music.value.id,
      title: music.value.title,
      audio_url: music.value.audio_url,
      image_url: music.value.image_url
    };

    // 触发全局播放事件
    const event = new CustomEvent('play-music', { detail: playInfo });
    window.dispatchEvent(event);
  } else {
    // 触发暂停事件
    const event = new CustomEvent('toggle-play-state', {
      detail: {
        id: music.value.id,
        isPlaying: false
      }
    });
    window.dispatchEvent(event);
  }
};

const handlePlaybackState = ((event: CustomEvent) => {
  const sameTrack = event.detail.id === music.value?.id;
  isPlaying.value = sameTrack && Boolean(event.detail.isPlaying);
  isAudioLoading.value = sameTrack && Boolean(event.detail.isLoading);
}) as EventListener;
watch(() => music.value?.id, () => {
  if (import.meta.client) window.dispatchEvent(new Event('request-music-player-state'));
});

// 监听播放状态变化
onMounted(() => {
  songHasMounted = true;
  songPageActive.value = true;
  const visibleCover = [desktopCoverImage.value].find(image => image && image.offsetWidth > 0);
  if (music.value?.image_url && visibleCover?.complete && !visibleCover.naturalWidth) coverError.value = true;
  // 仅在 taskId 存在（私有任务数据）或 SSR 未成功获取到数据时才在客户端拉取
  if ((isPrivateSong.value && canReadPrivateSong.value) || (!isPrivateSong.value && isLoading.value && !music.value)) {
    void loadMusicData();
  }

  if (music.value) {
    trackEvent('song_detail_view', { source: 'song_detail', page_path: route.path, song_id: musicId.value as string, tags: music.value?.tags });
  } else {
    trackEvent('song_detail_unavailable_view', { source: 'song_detail', page_path: route.path, song_id: musicId.value as string });
  }

  // 监听播放状态变化事件
  window.addEventListener('playing-state-changed', handlePlaybackState);
  window.dispatchEvent(new Event('request-music-player-state'));
});

onActivated(() => {
  if (songPageActive.value) return;
  songPageActive.value = true;
  if (music.value && loadedSongTarget !== songTargetKey.value) {
    music.value = null;
    taskVersions.value = [];
    loadedTaskOwnerId.value = '';
    taskHasDisplayName.value = false;
    loadedPrivateIdentity = '';
  }
  if ((isPrivateSong.value && canReadPrivateSong.value && (!music.value || loadedPrivateIdentity !== songSessionIdentity.value))
    || (!isPrivateSong.value && !music.value && (musicLoadError.value || loadedSongTarget !== songTargetKey.value))) void loadMusicData();
  window.dispatchEvent(new Event('request-music-player-state'));
});
onDeactivated(() => {
  songPageActive.value = false;
  invalidateSongRead();
  cancelSongDownload();
});

// 组件卸载时清理
onUnmounted(() => {
  songPageActive.value = false;
  invalidateSongRead();
  cancelSongDownload();
  window.removeEventListener('playing-state-changed', handlePlaybackState);
});

// Register head effects during setup; asynchronous task results only update refs.
const songSeoTags = computed(() => music.value?.tags?.split(',').map(tag => tag.trim()).join(',') || '');
const songSeoDescription = computed(() => music.value ? t('meta.musicSongId.description', { title: music.value.title, tags: songSeoTags.value }) : undefined);
const songSeoOgTitle = computed(() => music.value ? t('meta.musicSongId.ogTitle', { title: music.value.title }) : undefined);
const songSeoOgDescription = computed(() => music.value ? t('meta.musicSongId.ogDescription', { description: music.value.description?.substring(0, 150) || t('music.onlineListen') }) : undefined);
useSeoMeta({
  title: () => music.value ? t('meta.musicSongId.title', { title: music.value.title }) : undefined,
  description: () => songSeoDescription.value,
  keywords: () => music.value ? t('meta.musicSongId.keywords', { title: music.value.title, tags: songSeoTags.value }) : undefined,
  ogTitle: () => songSeoOgTitle.value,
  ogDescription: () => songSeoOgDescription.value,
  ogType: 'music.song',
  ogUrl: () => music.value ? `${siteUrl}/music/song/${music.value.id}` : undefined,
  ogImage: () => music.value?.image_url || `${siteUrl}/images/ogimage.png`,
  ogLocale: () => localeMappings.shortToFull[locale.value as keyof typeof localeMappings.shortToFull] || 'en_US',
  ogSiteName: 'MusicMint',
  twitterCard: 'summary_large_image',
  twitterTitle: () => songSeoOgTitle.value,
  twitterDescription: () => songSeoOgDescription.value,
  twitterImage: () => music.value?.image_url || `${siteUrl}/images/ogimage.png`,
});
useHead(() => !music.value ? { htmlAttrs: { lang: locale.value } } : {
  htmlAttrs: { lang: locale.value },
  link: [{ key: 'canonical', rel: 'canonical', href: `${siteUrl}${route.path}` }],
  script: [{
    key: 'song-schema', type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org', '@type': 'MusicComposition',
      name: music.value.title, composer: { '@type': 'Person', name: music.value.display_name },
      datePublished: music.value.releaseDate, description: music.value.description,
      genre: music.value.tags?.split(',').map(tag => tag.trim()), image: music.value.image_url,
      url: `${siteUrl}/music/song/${music.value.id}`,
      potentialAction: { '@type': 'ListenAction', target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/music/song/${music.value.id}` } },
      mainEntity: {
        '@type': 'MusicRecording', name: music.value.title,
        byArtist: { '@type': 'Person', name: music.value.display_name },
        duration: music.value.duration ? `PT${Math.floor(music.value.duration / 60)}M${Math.floor(music.value.duration % 60)}S` : undefined,
        isrcCode: music.value.id,
        recordingOf: { '@type': 'MusicComposition', name: music.value.title, description: music.value.description },
      },
    }),
  }],
});

const isDownloading = ref(false);
const downloadError = ref('');
const downloadNeedsMembership = ref(false);
let songDownloadRequest = 0;
let songDownloadController: AbortController | undefined;
const cancelSongDownload = () => {
  ++songDownloadRequest;
  songDownloadController?.abort();
  isDownloading.value = false;
};
const handleDownloadMusic = async (song: MusicViewModel) => {
  if (isDownloading.value || !songPageActive.value || route.path !== readingPath || song.id !== music.value?.id) return;
  downloadError.value = '';
  downloadNeedsMembership.value = false;
  if (!user.value) { toast.error(t('user.loginToDownloadMusic')); await handleSignIn(); return; }
  if (!userAccount.value) { downloadError.value = t('common.load_failed'); return; }
  if (userAccount.value.vip_level <= 0) {
    downloadNeedsMembership.value = true;
    downloadError.value = t('user.notEnoughVip');
    trackEvent('download_blocked_membership', { surface: 'song_detail', music_id: song.id });
    openPaywall({ trigger: 'download', surface: 'song_detail' });
    return;
  }
  if (!song.audio_url) { downloadError.value = t('music.downloadFailed'); return; }
  const request = ++songDownloadRequest;
  const identity = songSessionIdentity.value;
  const controller = new AbortController();
  songDownloadController = controller;
  const stillCurrent = () => request === songDownloadRequest && !controller.signal.aborted
    && songPageActive.value && route.path === readingPath
    && identity === songSessionIdentity.value && music.value?.id === song.id;
  isDownloading.value = true;
  toast.info(t('music.downloadStarted'));
  try {
    const response = await fetch(song.audio_url, { signal: controller.signal });
    if (!stillCurrent()) return;
    if (!response.ok) throw new Error('Audio download unavailable');
    const blob = await response.blob();
    if (!stillCurrent()) return;
    if (!blob.size) throw new Error('Empty audio');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${song.title || 'music'}.mp3`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(t('music.downloadSuccess'));
  } catch {
    if (!stillCurrent()) return;
    downloadError.value = t('music.downloadFailed');
    toast.error(downloadError.value);
  } finally { if (request === songDownloadRequest) isDownloading.value = false; }
};
const retryDownloadMusic = async (song: MusicViewModel) => {
  if (isDownloading.value || !songPageActive.value || route.path !== readingPath || song.id !== music.value?.id) return;
  const request = ++songDownloadRequest;
  const identity = songSessionIdentity.value;
  const stillCurrent = () => request === songDownloadRequest && songPageActive.value && route.path === readingPath
    && identity === songSessionIdentity.value && music.value?.id === song.id;
  isDownloading.value = true;
  try { await refreshUserAccount(); }
  catch { if (stillCurrent()) downloadError.value = t('common.load_failed'); return; }
  finally { if (request === songDownloadRequest) isDownloading.value = false; }
  if (!stillCurrent()) return;
  await handleDownloadMusic(song);
};

// Tag-to-discover page matching
const tagToDiscoverMap: Array<{ keywords: string[]; slug: string }> = [
  { keywords: ['rap', 'hip hop', 'hip-hop'], slug: 'ai-rap-song-generator' },
  { keywords: ['pop'], slug: 'ai-pop-song-generator' },
  { keywords: ['country'], slug: 'ai-country-song-generator' },
  { keywords: ['rock'], slug: 'ai-rock-song-generator' },
  { keywords: ['anime', 'jpop', 'j-pop'], slug: 'ai-anime-song-generator' },
  { keywords: ['lofi', 'lo-fi', 'chill'], slug: 'ai-lofi-music-generator' },
  { keywords: ['jazz'], slug: 'ai-jazz-music-generator' },
  { keywords: ['edm', 'electronic', 'dance'], slug: 'ai-edm-generator' },
  { keywords: ['birthday'], slug: 'ai-birthday-song-generator' },
  { keywords: ['love', 'romantic'], slug: 'ai-love-song-generator' },
  { keywords: ['funny', 'comedy'], slug: 'ai-funny-song-generator' },
];

const relatedDiscoverPages = computed(() => {
  if (!music.value?.tags) return [];
  const tagsLower = music.value.tags.toLowerCase();
  const matched: SeoLandingPage[] = [];

  for (const mapping of tagToDiscoverMap) {
    if (mapping.keywords.some(kw => tagsLower.includes(kw))) {
      const page = seoLandingPages.find(p => p.slug === mapping.slug);
      if (page) matched.push(page);
    }
    if (matched.length >= 4) break;
  }
  return matched;
});

const handleCreateSimilar = () => {
  if (!music.value) return;
  trackEvent('song_detail_create_similar_click', { source: 'song_detail', page_path: route.path, song_id: music.value.id, tags: music.value.tags });
  saveMusicData({
    prompt: music.value.description,
    tags: music.value.tags,
    title: music.value.title,
    mode: 'normal',
    source: 'song_detail',
    sourceSongId: music.value.id,
  }, route.path);
};

watch(songSessionIdentity, resetSongSession, { immediate: process.client, flush: 'sync' });
watch([musicId, taskId], () => {
  if (!songPageActive.value || route.path !== readingPath) return;
  invalidateSongRead();
  cancelSongDownload();
  music.value = null;
  taskVersions.value = [];
  loadedTaskOwnerId.value = '';
  taskHasDisplayName.value = false;
  loadedPrivateIdentity = '';
  loadedSongTarget = '';
  downloadError.value = '';
  downloadNeedsMembership.value = false;
  void loadMusicData();
}, { flush: 'sync' });
</script>

<template>
  <div ref="songRoot" class="musicmint-tool music-detail-page">
    <div class="song-container">
      <button type="button" class="song-back" @click="router.back()">
        <span class="i-lucide-arrow-left song-icon" aria-hidden="true" />
        {{ t('music.back') }}
      </button>

      <div v-if="isLoading" role="status" class="song-load-state">
        <span class="i-lucide-loader-2 song-icon animate-spin motion-reduce:animate-none" aria-hidden="true" />
        <h1 class="song-state-title">{{ t('ux.loadingSong') }}</h1>
      </div>
      <div v-else-if="needsSongSignIn" class="song-load-state">
        <h1 ref="songHeading" tabindex="-1" class="song-state-title">{{ t('ux.signInForSong') }}</h1>
        <button type="button" class="musicmint-primary-button" @click="handleSignIn">{{ t('user.login') }}</button>
      </div>
      <div v-else-if="musicLoadError" class="song-load-state">
        <h1 ref="songHeading" tabindex="-1" class="song-state-title">{{ t('ux.songLoadFailedTitle') }}</h1>
        <p role="alert">{{ t('ux.songLoadFailedDescription') }}</p>
        <button type="button" class="musicmint-secondary-button" @click="loadMusicData">{{ t('ux.retry') }}</button>
      </div>

      <template v-else-if="music">
        <div class="song-workspace">
          <header class="song-identity">
            <h1 id="song-title" ref="songHeading" tabindex="-1">{{ music.title }}</h1>
            <div class="song-metadata">
              <span class="song-creator" :aria-label="t('music.creator') + ': ' + music.display_name">{{ music.display_name }}</span>
              <span v-if="currentVersion" class="song-current-version">{{ t('ux.version', { version: String.fromCharCode(65 + currentVersion.position) }) }}</span>
              <span class="song-duration"><span class="i-lucide-clock-3 song-icon" aria-hidden="true" />{{ formatDuration(music.duration) }}</span>
              <time :datetime="music.releaseDate" :aria-label="t('music.releaseDate')">{{ music.releaseDate ? formatDate(music.releaseDate) : t('music.unknown') }}</time>
            </div>
            <ul v-if="songStyleTags.length" class="song-style-tags" :aria-label="t('ux.songStyleDetails')">
              <li v-for="tag in songStyleTags" :key="tag">{{ tag }}</li>
            </ul>
            <div class="song-actions">
              <button type="button" class="musicmint-primary-button song-play" @click="playMusic" :aria-pressed="isPlaying">
                <span v-if="isAudioLoading" class="i-lucide-loader-2 song-icon animate-spin motion-reduce:animate-none" aria-hidden="true" />
                <span v-else :class="isPlaying ? 'i-lucide-pause' : 'i-lucide-play'" class="song-icon" aria-hidden="true" />
                {{ isAudioLoading ? t('ux.cancelPlayback') : isPlaying ? t('music.pause') : t('music.play') }}
              </button>
              <button type="button" class="song-download" :aria-label="t('music.download')" :disabled="isDownloading"
                :aria-busy="isDownloading" @click="handleDownloadMusic(music)">
                <span class="i-lucide-download song-icon" aria-hidden="true" />
              </button>
              <button type="button" class="musicmint-secondary-button song-similar" @click="handleCreateSimilar">
                <span class="i-lucide-copy-plus song-icon" aria-hidden="true" />
                {{ t('music.createSimilar') }}
              </button>
            </div>
            <div v-if="downloadError" role="alert" class="song-download-feedback">
              <p>{{ downloadError }}</p>
              <NuxtLink v-if="downloadNeedsMembership" :to="pricingPath('song_detail_download')">{{ t('nav.pricing') }}</NuxtLink>
              <button v-else type="button" class="musicmint-secondary-button" :disabled="isDownloading" @click="retryDownloadMusic(music)">{{ t('ux.retry') }}</button>
            </div>
          </header>

          <div class="song-sleeve">
            <div class="song-cover">
              <img ref="desktopCoverImage" :src="coverSrc" :srcset="coverSrcset" :sizes="coverSizes" :alt="music.title"
                width="360" height="360" fetchpriority="high" loading="eager" decoding="async" @error="coverError = true" />
            </div>
            <nav v-if="taskVersions.length > 1" class="song-versions" :aria-label="music.title">
              <NuxtLink v-for="version in taskVersions" :key="version.id" replace
                :to="{ path: `/music/song/${version.id}`, query: { taskId } }"
                :aria-current="version.id === music.id ? 'page' : undefined"
                :aria-label="`${music.title} · ${t('ux.version', { version: String.fromCharCode(65 + version.position) })} · ${formatDuration(version.duration)}`">
                <span class="song-version-cover">
                  <img v-if="version.image_url && !versionCoverErrors[version.id]" :src="musicCoverUrl(version.image_url, 160)" alt=""
                    width="48" height="48" loading="lazy" decoding="async" @error="versionCoverErrors[version.id] = true" />
                  <span v-else class="i-lucide-music-2 song-icon" aria-hidden="true" />
                </span>
                <span class="song-version-name">{{ t('ux.version', { version: String.fromCharCode(65 + version.position) }) }}</span>
                <span class="song-version-duration">{{ formatDuration(version.duration) }}</span>
                <span v-if="version.id === music.id" class="i-lucide-circle-check song-version-selected song-icon" aria-hidden="true" />
              </NuxtLink>
            </nav>
          </div>

          <section class="song-lyrics" aria-labelledby="song-lyrics-title">
            <h2 id="song-lyrics-title">{{ t('music.lyrics') }}</h2>
            <nav v-if="lyricsSections.filter(section => section.heading).length > 1" class="song-chapters" :aria-label="t('music.lyrics')">
              <template v-for="(section, sectionIndex) in lyricsSections" :key="`chapter-${sectionIndex}`">
                <a v-if="section.heading" :href="`#song-lyric-${sectionIndex}`">{{ section.heading }}</a>
              </template>
            </nav>
            <div v-if="lyricsSections.length > 0" class="lyrics-content">
              <section v-for="(section, sectionIndex) in lyricsSections" :id="`song-lyric-${sectionIndex}`" :key="`${section.heading || 'lyrics'}-${sectionIndex}`" tabindex="-1" class="lyrics-section">
                <details v-if="section.heading.length > 80" class="lyrics-notes">
                  <summary>
                    <span>{{ section.heading }}</span>
                    <span class="i-lucide-chevron-down song-icon" aria-hidden="true" />
                  </summary>
                  <p>{{ section.heading }}</p>
                </details>
                <h3 v-else-if="section.heading" class="lyrics-section-title">{{ section.heading }}</h3>
                <div class="lyrics-lines">
                  <p v-for="(line, lineIndex) in section.lines" :key="`${sectionIndex}-${lineIndex}`" class="lyric-line">{{ line }}</p>
                </div>
              </section>
            </div>
            <p v-else class="song-empty-lyrics">{{ t('music.noLyrics') }}</p>
          </section>

          <aside class="song-context">
            <details v-if="music.tags" class="song-style">
              <summary>
                <span class="song-style-title">{{ t('ux.songStyleDetails') }}</span>
                <span class="song-style-preview" aria-hidden="true">{{ music.tags }}</span>
                <span class="i-lucide-chevron-down song-icon" aria-hidden="true" />
              </summary>
              <p>{{ music.tags }}</p>
            </details>
            <section v-if="relatedDiscoverPages.length > 0" class="song-related" aria-labelledby="song-related-title">
              <h2 id="song-related-title">{{ t('ux.songRelatedGenerators') }}</h2>
              <div class="song-related-links">
                <NuxtLink v-for="page in relatedDiscoverPages" :key="page.slug" :to="`/discover/${page.slug}`"><span>{{ page.h1 }}</span><span class="i-lucide-arrow-up-right song-icon" aria-hidden="true" /></NuxtLink>
              </div>
            </section>
          </aside>
        </div>
      </template>

      <div v-else class="song-unavailable">
        <h1 ref="songHeading" tabindex="-1">{{ t('music.unavailableTitle') }}</h1>
        <p>{{ t('music.unavailableDescription') }}</p>
        <div class="song-unavailable-actions">
          <button type="button" class="musicmint-primary-button" @click="router.push('/music/global-trending')">{{ t('music.exploreTrending') }}</button>
          <button type="button" class="musicmint-secondary-button" @click="router.push('/create')">{{ t('music.createOwnSong') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.music-detail-page { width: 100%; min-width: 0; padding-bottom: 48px; }
.song-container { width: 100%; max-width: 1120px; margin-inline: auto; }
.song-back { display: inline-flex; align-items: center; gap: 8px; margin-bottom: 24px; padding: 8px 0; border: 0; border-radius: 6px; background: transparent; color: var(--mint-muted); font-size: 14px; }
.song-back:hover { color: var(--mint-ink); }
.song-icon { display: inline-block; width: 18px; height: 18px; flex: none; }
.song-workspace { --song-art-width: 360px; display: grid; grid-template-columns: var(--song-art-width) minmax(0, 1fr); grid-template-rows: auto auto auto; gap: 32px 64px; align-items: start; }
.song-identity { grid-column: 2; grid-row: 1; min-width: 0; padding-top: 8px; }
.song-identity h1:focus, .song-unavailable h1:focus { outline: none; }
.song-identity h1 { margin: 0; max-width: 22ch; color: var(--mint-ink); font-size: 52px; line-height: 1.12; font-weight: 700; letter-spacing: -.03em; overflow-wrap: anywhere; text-wrap: balance; }
.song-metadata { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; margin-top: 20px; color: var(--mint-muted); font-size: 14px; line-height: 1.5; }
.song-creator { color: var(--mint-ink); font-weight: 500; }
.song-current-version { color: var(--mint-focus); }
.song-duration { display: inline-flex; align-items: center; gap: 6px; font-variant-numeric: tabular-nums; }
.song-duration .song-icon { width: 14px; height: 14px; }
.song-metadata time { flex-basis: 100%; font-size: 13px; }
.song-style-tags { display: flex; flex-wrap: wrap; gap: 6px 12px; margin: 16px 0 0; padding: 0; list-style: none; }
.song-style-tags li { max-width: 100%; color: var(--mint-muted); font-size: 13px; line-height: 1.6; overflow-wrap: anywhere; }
.song-style-tags li:not(:last-child)::after { content: '/'; margin-inline-start: 12px; color: var(--mint-border); }
.song-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 24px; }
.song-actions button { min-height: 48px; font-size: 14px; }
.song-play { min-width: 112px; }
.song-download { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; padding: 0; color: var(--mint-ink); background: transparent; border: 1px solid var(--mint-border); border-radius: var(--mint-radius); }
.song-download:hover { color: var(--mint-focus); border-color: var(--mint-focus); background: var(--mint-surface); }
.song-download:disabled { opacity: .5; cursor: default; }
.song-sleeve { grid-column: 1; grid-row: 1 / 3; min-width: 0; }
.song-cover { aspect-ratio: 1; border-radius: 12px; overflow: hidden; background: var(--mint-surface); }
.song-cover img { display: block; width: 100%; height: 100%; object-fit: cover; }
.song-versions { display: grid; gap: 4px; margin-top: 24px; }
.song-versions a { display: flex; min-width: 0; min-height: 72px; align-items: center; gap: 12px; padding: 10px 12px; border: 1px solid transparent; border-radius: 10px; color: var(--mint-muted); font-size: 14px; text-decoration: none; }
.song-versions a[aria-current="page"] { color: var(--mint-ink); border-color: var(--mint-border); background: var(--mint-surface); }
.song-versions a:hover { color: var(--mint-ink); background: var(--mint-surface); }
.song-version-cover { display: flex; width: 48px; height: 48px; align-items: center; justify-content: center; flex: none; border-radius: 6px; overflow: hidden; background: var(--mint-surface-raised); }
.song-version-cover img { width: 100%; height: 100%; object-fit: cover; }
.song-version-name { min-width: 0; overflow-wrap: anywhere; }
.song-version-duration { margin-inline-start: auto; font-variant-numeric: tabular-nums; white-space: nowrap; }
.song-version-selected { color: var(--mint-focus); }
.song-download-feedback { margin-top: 20px; padding: 16px; border: 1px solid #f871714d; border-radius: 12px; color: #fca5a5; font-size: 14px; line-height: 1.6; }
.song-download-feedback :is(a, button) { display: inline-flex; min-height: 44px; align-items: center; margin-top: 8px; }
.song-download-feedback a { color: var(--mint-focus); text-decoration: underline; text-underline-offset: 3px; }
.song-lyrics { grid-column: 2; grid-row: 2 / 4; min-width: 0; padding-top: 28px; border-top: 1px solid var(--mint-border); }
.song-lyrics h2 { margin: 0 0 12px; font-size: 22px; font-weight: 600; line-height: 1.4; }
.song-chapters { display: flex; flex-wrap: wrap; gap: 4px 20px; margin: 0 0 24px; padding-bottom: 12px; border-bottom: 1px solid var(--mint-border); }
.song-chapters a { display: inline-flex; min-height: 44px; max-width: 100%; align-items: center; padding: 8px 0; color: var(--mint-muted); font-size: 13px; line-height: 1.6; text-decoration: none; overflow-wrap: anywhere; }
.song-chapters a:hover { color: var(--mint-ink); text-decoration: underline; text-underline-offset: 6px; }
.lyrics-content { max-width: 70ch; }
.lyrics-section { display: grid; grid-template-columns: 72px minmax(0, 1fr); column-gap: 24px; scroll-margin-top: 28px; }
.lyrics-section:focus-visible { outline: 2px solid var(--mint-focus); outline-offset: 8px; border-radius: 4px; }
.lyrics-section + .lyrics-section { margin-top: 32px; }
.lyrics-section-title { margin: 5px 0 0; color: var(--mint-focus); font-size: 13px; line-height: 1.6; font-weight: 500; overflow-wrap: anywhere; }
.lyrics-lines { min-width: 0; grid-column: 2; }
.lyric-line { margin: 0; color: var(--mint-ink); font-size: 20px; line-height: 1.75; overflow-wrap: anywhere; }
.lyric-line + .lyric-line { margin-top: 2px; }
.song-empty-lyrics { color: var(--mint-muted); font-size: 16px; line-height: 1.8; }
.song-context { grid-column: 1; grid-row: 3; min-width: 0; padding-top: 24px; border-top: 1px solid var(--mint-border); }
.song-style summary { display: flex; flex-wrap: wrap; min-height: 44px; align-items: center; gap: 12px; padding-bottom: 12px; color: var(--mint-ink); font-size: 14px; line-height: 1.6; cursor: pointer; list-style: none; }
.song-style-title { flex: 1; min-width: 0; font-size: 15px; font-weight: 600; }
.song-style-preview { order: 3; flex-basis: 100%; min-width: 0; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; color: var(--mint-muted); }
.song-style[open] .song-style-preview { display: none; }
.song-style summary > :last-child { margin-inline-start: auto; }
.song-style p { margin: 4px 0 0; max-width: 70ch; color: var(--mint-muted); font-size: 14px; line-height: 1.8; overflow-wrap: anywhere; }
.song-related { margin-top: 28px; padding-top: 24px; border-top: 1px solid var(--mint-border); }
.song-related h2 { margin: 0 0 12px; font-size: 15px; line-height: 1.4; font-weight: 600; }
.song-related-links { display: grid; gap: 8px; }
.song-related-links a { display: flex; min-height: 44px; align-items: center; justify-content: space-between; gap: 12px; color: var(--mint-focus); font-size: 14px; line-height: 1.5; text-decoration: none; }
.song-related-links a:hover > :first-child { text-decoration: underline; text-underline-offset: 4px; }
.lyrics-notes { grid-column: 1 / -1; margin-bottom: 16px; }
.lyrics-notes summary { display: flex; min-height: 44px; align-items: flex-start; gap: 16px; padding-block: 8px; color: var(--mint-muted); font-size: 14px; line-height: 1.7; cursor: pointer; list-style: none; }
.lyrics-notes summary > :first-child { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; flex: 1; }
.lyrics-notes summary > :last-child { margin-top: 4px; }
.lyrics-notes p { margin-top: 8px; color: var(--mint-muted); font-size: 14px; line-height: 1.8; overflow-wrap: anywhere; }
.song-style summary::-webkit-details-marker, .lyrics-notes summary::-webkit-details-marker { display: none; }
:is(.song-style, .lyrics-notes)[open] summary > :last-child { transform: rotate(180deg); }
:is(.song-style, .lyrics-notes) summary:focus-visible { outline: 2px solid var(--mint-focus); outline-offset: 3px; border-radius: 4px; }
.song-load-state { min-height: 320px; display: flex; flex-direction: column; align-items: flex-start; justify-content: center; gap: 16px; color: var(--mint-muted); font-size: 14px; }
.song-state-title { margin: 0; max-width: 30ch; color: var(--mint-ink); font-size: 24px; line-height: 1.35; font-weight: 650; }
.song-load-state p { margin: 0; max-width: 50ch; font-size: 15px; line-height: 1.7; }
.song-unavailable { max-width: 70ch; padding-block: 56px; }
.song-unavailable h1 { margin: 0; font-size: 28px; line-height: 1.25; font-weight: 650; }
.song-unavailable > p { margin-top: 16px; color: var(--mint-muted); font-size: 16px; line-height: 1.7; }
.song-unavailable-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
.music-detail-page ::selection { color: var(--mint-ink); background: var(--mint-accent-strong); }
@media (min-width: 1024px) and (max-width: 1279px) {
  .song-workspace { --song-art-width: 320px; gap: 32px 40px; }
  .song-identity h1 { font-size: 44px; }
}
@media (min-width: 768px) and (max-width: 1023px) {
  .song-workspace { --song-art-width: 240px; gap: 28px 32px; }
  .song-identity h1 { font-size: 36px; }
  .song-actions { gap: 8px; }
  .song-actions .musicmint-secondary-button { padding-inline: 12px; }
  .song-versions a { gap: 8px; padding-inline: 8px; }
  .lyrics-section { display: block; }
  .lyrics-section-title { margin: 0 0 10px; }
  .lyric-line { font-size: 18px; }
}
@media (max-width: 767px) {
  .song-back { margin-bottom: 16px; }
  .song-workspace { display: flex; flex-direction: column; gap: 24px; }
  .song-sleeve { display: contents; }
  .song-cover { order: 1; width: 260px; max-width: 100%; align-self: center; }
  .song-identity { order: 2; width: 100%; padding-top: 0; }
  .song-identity h1 { font-size: 28px; line-height: 1.18; }
  .song-metadata { gap: 6px 12px; margin-top: 12px; font-size: 13px; }
  .song-metadata time { flex-basis: auto; font-size: 12px; }
  .song-style-tags { margin-top: 12px; gap: 4px 8px; }
  .song-style-tags li { font-size: 12px; }
  .song-style-tags li:not(:last-child)::after { margin-inline-start: 8px; }
  .song-actions { gap: 8px; margin-top: 20px; }
  .song-actions .musicmint-secondary-button { padding-inline: 14px; }
  .song-versions { order: 3; display: flex; flex-wrap: wrap; width: 100%; gap: 8px; margin-top: 0; }
  .song-versions a { flex: 1 1 140px; gap: 8px; min-height: 64px; padding: 8px; border-color: var(--mint-border); font-size: 12px; }
  .song-version-cover { width: 36px; height: 36px; }
  .song-version-selected { display: none; }
  .song-versions a[aria-current="page"] { border-color: var(--mint-focus); }
  .song-lyrics { order: 4; width: 100%; padding-top: 24px; }
  .song-lyrics h2 { font-size: 20px; }
  .song-chapters { gap: 4px 20px; margin-bottom: 24px; }
  .lyrics-section { display: block; }
  .lyrics-section-title { margin: 0 0 10px; }
  .lyrics-section + .lyrics-section { margin-top: 28px; }
  .lyric-line { font-size: 18px; }
  .song-context { order: 5; width: 100%; padding-top: 24px; }
}
</style>
